import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'

const storageKey = (id) => `whatsNewDismissed:${id}`

const readDismissed = (id) => {
  try {
    return localStorage.getItem(storageKey(id)) === 'true'
  } catch {
    return false
  }
}

/**
 * True until the end of the `until` day (local time), so a notice released
 * with a hardcoded date disappears on its own afterwards.
 */
export const isWhatsNewActive = (until) => {
  if (!until) return false
  const end = new Date(`${until}T23:59:59`)
  return !Number.isNaN(end.getTime()) && new Date() <= end
}

/**
 * Small "New" pill to mark a changed control while the notice is active.
 */
export function NewBadge({ until, className = '' }) {
  const { t } = useTranslation()
  if (!isWhatsNewActive(until)) return null
  return (
    <span className={`inline-flex items-center rounded-full bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-strong ${className}`}>
      {t('shared:whatsNew.badge')}
    </span>
  )
}

/**
 * Compact "what's new" strip listing what changed in an area.
 * Hidden after `until` or once the user closes it (remembered per browser).
 */
export default function WhatsNew({ id, until, title, items = [] }) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => readDismissed(id))

  if (dismissed || !isWhatsNewActive(until) || items.length === 0) return null

  const handleDismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(storageKey(id), 'true')
    } catch {
      // Storage unavailable: the notice just comes back on the next visit
    }
  }

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2 text-sm text-gray-700"
    >
      <span className="mt-0.5 shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
        {t('shared:whatsNew.badge')}
      </span>
      <div className="min-w-0 flex-1">
        {title && <span className="font-medium text-gray-900">{title} </span>}
        <span className="text-gray-600">{items.join(' · ')}</span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('shared:whatsNew.dismiss')}
        className="shrink-0 rounded p-1 text-gray-400 hover:bg-blue-100 hover:text-gray-600 transition-colors"
      >
        <FaTimes className="h-3 w-3" />
      </button>
    </div>
  )
}
